import { CircularProgress } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { logJoinAuditRoom, logLeaveAuditRoom } from 'src/clinic/components/CheckIn/services'
import { markAuditorBookedEventCheckInLog } from 'src/consumer/services/featureServices'
import {
  AuditAgoraVideoSurface,
  FLOW_TYPES,
  preloadAgoraRtcUIKitModule,
  resolveSessionRoutingContext,
  RTC_PROVIDERS,
} from 'src/live'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import { buildAuditorRtmUsername } from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/utils/rtmUsernameBuilder'

function readStoredAuditRoomDetails() {
  const rawAuditRoomDetails = localStorage.getItem('auditRoomDetails')

  if (!rawAuditRoomDetails) {
    return null
  }

  try {
    return JSON.parse(rawAuditRoomDetails)
  } catch (error) {
    console.error('Failed to parse auditRoomDetails from localStorage', error)
    return null
  }
}

export default function MentorClinicAuditChamberRoom() {
  const { chamberRoomId, channelName } = useParams()
  const { mentorProfileData } = useContext(MentorProfileContext)
  const [agoraRtcProps, setAgoraRtcProps] = useState({})
  const [agoraRtmProps, setAgoraRtmProps] = useState({})
  const [auditSessionBootstrap, setAuditSessionBootstrap] = useState(null)
  const [auditSessionRoutingDecision, setAuditSessionRoutingDecision] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sessionStarted, setSessionStarted] = useState(false)
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [isAudioOpen, setIsAudioOpen] = useState(false)
  const auditRoomDetails = useMemo(() => readStoredAuditRoomDetails(), [])
  const auditRoutingContext = useMemo(
    () =>
      resolveSessionRoutingContext({
        flowType: FLOW_TYPES.auditSession,
        sessionRoutingDecision: auditSessionRoutingDecision,
      }),
    [auditSessionRoutingDecision],
  )
  const resolvedAuditSessionRoutingDecision = auditRoutingContext?.sessionRoutingDecision || null
  const auditObserverJoinEnabled =
    resolvedAuditSessionRoutingDecision?.capabilities?.auditObserverJoin === true
  const auditRtcProviderResolved = auditRoutingContext?.rtcProviderResolved
  const auditRtcProviderSupported = auditRtcProviderResolved === RTC_PROVIDERS.agora
  const useAppOwnedAuditRtcSurface = auditObserverJoinEnabled && auditRtcProviderSupported
  const auditRtcSurfaceFallback = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        background:
          'linear-gradient(135deg, rgba(248, 250, 252, 1) 0%, rgba(243, 244, 255, 1) 100%)',
        color: '#4c1d95',
      }}
    >
      <CircularProgress sx={{ color: '#7c3aed' }} />
      <div>Preparing your audit session...</div>
    </div>
  )

  const handleRefreshStatusForUser = useCallback(() => {
    markAuditorBookedEventCheckInLog(
      mentorProfileData?.id,
      auditRoomDetails?.checkedInMentorId,
      'REFRESH',
      {
        camera: isCameraOpen,
        mic: isAudioOpen,
      },
    )
  }, [auditRoomDetails?.checkedInMentorId, isAudioOpen, isCameraOpen, mentorProfileData?.id])

  const agoraCallbacks = {
    EndCall: async () => {
      await logLeaveAuditRoom(auditRoomDetails)
      await markAuditorBookedEventCheckInLog(
        mentorProfileData?.id,
        auditRoomDetails?.checkedInMentorId,
        'LEAVE',
        {
          camera: isCameraOpen,
          mic: isAudioOpen,
        },
      )
      window.history.back()
    },
  } // contains Agora Engine events as keys and callback functions as their values.

  const getJoinData = useCallback(async () => {
    setLoading(true)
    const response = await getMPDataWithParams('/api/secure/teacher/audit/chamber-room/token', {
      chamberRoomId: chamberRoomId,
      channelName: channelName,
    })
    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const data = getDataMapFromCommonResponse(response)
      const responseRoutingContext = resolveSessionRoutingContext({
        flowType: FLOW_TYPES.auditSession,
        sessionRoutingDecision: data?.sessionRoutingDecision,
      })
      const responseRoutingDecision = responseRoutingContext?.sessionRoutingDecision || null
      const responseAuditObserverJoinEnabled =
        responseRoutingDecision?.capabilities?.auditObserverJoin === true
      const responseRtcProviderResolved = responseRoutingContext?.rtcProviderResolved

      if (!responseAuditObserverJoinEnabled) {
        toast.error('Audit observer join is not enabled for this session')
        window.history.back()
        setLoading(false)
        return
      }

      if (responseRtcProviderResolved !== RTC_PROVIDERS.agora) {
        toast.error('Audit sessions are currently pinned to the Agora provider')
        window.history.back()
        setLoading(false)
        return
      }

      await logJoinAuditRoom(auditRoomDetails)
      setAuditSessionBootstrap(data || null)
      setAuditSessionRoutingDecision(data?.sessionRoutingDecision || null)
      const resolvedVideoQuality =
        data?.sessionRoutingDecision?.videoQuality || data?.videoQuality || null
      setAgoraRtcProps({
        appId: data?.appId || import.meta.env.VITE_AGORA_APP_ID,
        channel: channelName,
        uid: parseInt(data?.uid),
        token: data?.rtcToken,
        role: 'host',
        enableDualStream: resolvedVideoQuality?.enableDualStream === true,
        enableAudio: false,
        enableVideo: false,
        videoQuality: resolvedVideoQuality,
      })
      // Build RTM username with role, name, and profilePicId for auditor
      const rtmUsername = buildAuditorRtmUsername(mentorProfileData)

      setAgoraRtmProps({
        username: rtmUsername,
        displayUsername: true,
        token: data?.rtmToken,
      })
      setSessionStarted(true)
    } else {
      toast.error('Failed to get join data')
      window.history.back()
    }
    setLoading(false)
  }, [auditRoomDetails, chamberRoomId, channelName, mentorProfileData])

  useEffect(() => {
    getJoinData()
    markAuditorBookedEventCheckInLog(
      mentorProfileData?.id,
      auditRoomDetails?.checkedInMentorId,
      'JOIN',
      {
        camera: false,
        mic: false,
      },
    )
  }, [
    auditRoomDetails?.checkedInMentorId,
    getJoinData,
    mentorProfileData?.id,
  ])

  // Heartbeat mechanism - log refresh status every 3 minutes
  useEffect(() => {
    let interval = null
    if (sessionStarted) {
      handleRefreshStatusForUser()
      interval = setInterval(() => {
        handleRefreshStatusForUser()
      }, 180000) // 3 minutes = 180000 ms
    } else {
      clearInterval(interval)
    }
    return () => {
      clearInterval(interval)
    }
  }, [handleRefreshStatusForUser, sessionStarted])

  useEffect(() => {
    if (!useAppOwnedAuditRtcSurface) {
      return
    }

    preloadAgoraRtcUIKitModule().catch(() => {})
  }, [useAppOwnedAuditRtcSurface])

  useEffect(() => {
    const debugPayload = {
      chamberRoomId: chamberRoomId || null,
      channelName: channelName || null,
      sessionStarted,
      auditObserverJoinEnabled,
      selectedSurface: useAppOwnedAuditRtcSurface
        ? 'app-owned-agora-surface'
        : 'unsupported-audit-surface',
      rtcProviderResolved: auditRtcProviderResolved || null,
      debugVersion: 'audit-session-shared-surface-v2',
    }

    if (typeof window !== 'undefined') {
      window.__AUMHUM_AUDIT_RTC_DEBUG__ = debugPayload
    }

    console.info('[MentorClinicAuditChamberRoom] RTC surface selection', debugPayload)
  }, [
    auditObserverJoinEnabled,
    auditRtcProviderResolved,
    channelName,
    chamberRoomId,
    sessionStarted,
    useAppOwnedAuditRtcSurface,
  ])

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <h3>Starting your audit session....</h3>
          <CircularProgress />
        </div>
      ) : (
        <AuditAgoraVideoSurface
          sessionBootstrap={auditSessionBootstrap}
          sessionRoutingDecision={resolvedAuditSessionRoutingDecision}
          rtcProps={agoraRtcProps}
          callbacks={agoraCallbacks}
          rtmProps={agoraRtmProps}
          onCameraClose={() => {
            setIsCameraOpen(false)
          }}
          onCameraOpen={() => {
            setIsCameraOpen(true)
          }}
          onAudioClose={() => {
            setIsAudioOpen(false)
          }}
          onAudioOpen={() => {
            setIsAudioOpen(true)
          }}
          fallback={auditRtcSurfaceFallback}
        />
      )}
    </div>
  )
}
